import { useEffect, useMemo, useState } from 'react';
import CustomButton from '../../button/button';
import { availableLanguages } from '../../data/languageData';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import successToast from '../../toasts/successToast';
import errorToast from '../../toasts/errorToast';
import { getWordTextBlocksAction, updateWordFileAction } from '../../../redux/actions/translationAction';
import { Spinner, Table } from 'react-bootstrap';
import DocxSourceTextBlock from './docx-source-text';
import DocxTextBlock from './docx-text-block';
import { filterUpdatedTextblocks } from '../../helper';

const DocxEditor = ({ documentData, sourceFileUrl, translatedFileUrl, job_id, isLoading }) => {
    const [translatedTextBlocks, setTranslatedTextBlocks] = useState([]);
    const [sourceTextBlocks, setSourceTextBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editedHistory, setEditedHistory] = useState(new Map());
    const [selectedId, setSelectedId] = useState(null);
    const [sentencePairs, setSentencePairs] = useState([]);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (translatedFileUrl) {
            setLoading(true);
            dispatch(getWordTextBlocksAction({ translatedFileUrl, sourceFileUrl }, onFetchSuccess, onError));
        }
    }, [translatedFileUrl, sourceFileUrl, dispatch]);

    // Function to split text into sentences while preserving original structure
    const splitIntoSentences = (text) => {
        if (!text) return [];
        // Split by sentence endings, keeping the punctuation
        return text.split(/(?<=[.!?])\s+/).filter(sentence => sentence.trim().length > 0);
    };

    // Group text blocks by paraId
    const groupTextBlocksByParaId = (textBlocks) => {
        const groupedByParaId = {};

        textBlocks.forEach(block => {
            if (!groupedByParaId[block.paraId]) {
                groupedByParaId[block.paraId] = [];
            }
            groupedByParaId[block.paraId].push(block);
        });

        return groupedByParaId;
    };

    // Create sentence pairs for display
    useEffect(() => {
        if (sourceTextBlocks.length && translatedTextBlocks.length) {
            const sourceGroups = groupTextBlocksByParaId(sourceTextBlocks);
            const translatedGroups = groupTextBlocksByParaId(translatedTextBlocks);

            const newSentencePairs = [];

            // Process each paragraph
            Object.keys(sourceGroups).forEach(paraId => {
                const sourceBlocks = sourceGroups[paraId];
                const translatedBlocks = translatedGroups[paraId] || [];

                // Find the main text block (the one with actual content)
                const mainSourceBlock = sourceBlocks.find(block => block.text && block.text.trim() !== '');
                const mainTranslatedBlock = translatedBlocks.find(block => block.text && block.text.trim() !== '');

                if (mainSourceBlock && mainTranslatedBlock) {
                    const sourceSentences = splitIntoSentences(mainSourceBlock.text);
                    const translatedSentences = splitIntoSentences(mainTranslatedBlock.text);

                    // Create pairs for each sentence
                    const maxSentences = Math.max(sourceSentences.length, translatedSentences.length);

                    for (let i = 0; i < maxSentences; i++) {
                        newSentencePairs.push({
                            paraId: paraId,
                            sentenceIndex: i,
                            source: sourceSentences[i] || "",
                            originalTranslated: translatedSentences[i] || "",
                            currentTranslated: translatedSentences[i] || "",
                            mainSourceBlock: mainSourceBlock,
                            mainTranslatedBlock: mainTranslatedBlock,
                            allSourceBlocks: sourceBlocks,
                            allTranslatedBlocks: translatedBlocks,
                            totalSentences: maxSentences
                        });
                    }
                }
            });

            setSentencePairs(newSentencePairs);
        }
    }, [sourceTextBlocks, translatedTextBlocks]);

    const onFetchSuccess = (data) => {
        setTranslatedTextBlocks(data.data.data.translatedTextBlocks || []);
        setSourceTextBlocks(data.data.data.sourceTextBlocks || []);
        setLoading(false);
    };

    const handleTextBlockUpdate = (index, updatedText) => {
        setSentencePairs(prev => {
            const newPairs = [...prev];
            if (newPairs[index]) {
                newPairs[index] = {
                    ...newPairs[index],
                    currentTranslated: updatedText
                };
            }
            return newPairs;
        });

        const sentencePair = sentencePairs[index];
        if (!sentencePair) return;

        setEditedHistory(prevHistory => {
            const newHistory = new Map(prevHistory);
            const historyKey = `${sentencePair.paraId}-${sentencePair.sentenceIndex}`;

            newHistory.set(historyKey, {
                source: sentencePair.source,
                translated: sentencePair.originalTranslated,
                updated: updatedText,
                paraId: sentencePair.paraId,
                sentenceIndex: sentencePair.sentenceIndex
            });

            return newHistory;
        });
    };

    // Reconstruct the original text block format for submission
    const getUpdatedTextBlocksForSubmission = () => {
        const updatedBlocks = [...translatedTextBlocks];
        const updatedParagraphs = new Map();

        // Group updates by paragraph
        Array.from(editedHistory.values()).forEach(update => {
            if (!updatedParagraphs.has(update.paraId)) {
                updatedParagraphs.set(update.paraId, []);
            }
            updatedParagraphs.get(update.paraId).push(update);
        });

        // Update each paragraph
        updatedParagraphs.forEach((sentenceUpdates, paraId) => {
            // Sort updates by sentence index
            sentenceUpdates.sort((a, b) => a.sentenceIndex - b.sentenceIndex);

            // Find all blocks for this paragraph
            const paragraphBlocks = updatedBlocks.filter(b => b.paraId === paraId);
            const mainBlockIndex = paragraphBlocks.findIndex(b => b.text && b.text.trim() !== '');

            if (mainBlockIndex !== -1) {
                const mainBlock = paragraphBlocks[mainBlockIndex];
                const originalSentences = splitIntoSentences(mainBlock.text);

                // Update the sentences that were edited
                sentenceUpdates.forEach(update => {
                    if (originalSentences[update.sentenceIndex]) {
                        originalSentences[update.sentenceIndex] = update.updated;
                    }
                });

                // Reconstruct the paragraph text
                const updatedText = originalSentences.join(' ');

                // Update the main block with reconstructed text
                const globalIndex = updatedBlocks.findIndex(b =>
                    b.paraId === paraId &&
                    b.id === mainBlock.id
                );

                if (globalIndex !== -1) {
                    updatedBlocks[globalIndex] = {
                        ...mainBlock,
                        text: updatedText
                    };
                }
            }
        });

        return updatedBlocks;
    };

    const handleSubmit = () => {
        setLoading(true);
        const updatedParts = Array.from(editedHistory.values());
        const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);

        if (updatedParts.length > 0) {
            const updatedTextBlocks = getUpdatedTextBlocksForSubmission();

            dispatch(updateWordFileAction({
                documentUrl: translatedFileUrl,
                updatedTextBlocks: updatedTextBlocks,
                updatedParts: filteredUpdatedParts,
                outputPath: `${job_id}.docx`,
                job_id: job_id
            }, onSuccess, onError));
        } else {
            navigate('/post-edit/summary', { state: { job_id } });
        }
    };

    const handleSaveDraft = () => {
        setLoading(true);
        const updatedParts = Array.from(editedHistory.values());
        const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);
        const updatedTextBlocks = getUpdatedTextBlocksForSubmission();

        dispatch(updateWordFileAction({
            documentUrl: translatedFileUrl,
            updatedTextBlocks: updatedTextBlocks,
            updatedParts: filteredUpdatedParts,
            outputPath: `${job_id}.docx`,
            job_id: job_id
        }, onSaveSuccess, onError));
    };

    const onSuccess = (data) => {
        setLoading(false);
        successToast(data.data.message);
        const job = data.data.data;
        navigate('/post-edit/summary', {
            state: {
                updatedParts: Array.from(editedHistory.values()),
                sourceLang: job.source_lang,
                targetLang: job.target_lang,
                job_id: job_id
            }
        });
        setEditedHistory(new Map());
    };

    const onSaveSuccess = (data) => {
        setEditedHistory(new Map());
        dispatch(getWordTextBlocksAction(
            { translatedFileUrl: data?.data?.data?.signed_url, sourceFileUrl },
            onFetchSuccess,
            onError
        ));
        successToast(data.data.message);
    };

    const onError = (error) => {
        setLoading(false);
        errorToast(error?.data?.message || 'An error occurred');
    };

    return (
        <div className='bg-white h-100 border rounded-4 m-2'>
            <div className='d-flex justify-content-between w-100 align-items-center p-2'>
                <div className='d-flex gap-4 w-100 align-items-center'>
                    <CustomButton
                        disabled={true}
                        variant='primary'
                        className='d-flex align-items-center cursor_pointer fs_12'
                        style={{ height: '30px' }}
                    >
                        {availableLanguages.find(lang => lang?.value === documentData?.source_lang)?.label || "Loading..."}
                    </CustomButton>
                </div>
                <div className='d-flex justify-content-between gap-4 w-100 align-items-center'>
                    <div className='px-3'>
                        <CustomButton
                            disabled={true}
                            variant='primary'
                            className='d-flex align-items-center cursor_pointer fs_12'
                            style={{ height: '30px' }}
                        >
                            {availableLanguages.find(lang => lang?.value === documentData?.target_lang)?.label || "Loading..."}
                        </CustomButton>
                    </div>
                    <div className='d-flex gap-3'>
                        <CustomButton
                            variant='primary'
                            disabled={loading}
                            className='d-flex align-items-center fs_12'
                            style={{ height: '30px' }}
                            onClick={handleSaveDraft}
                        >
                            Save Draft
                        </CustomButton>
                        <CustomButton
                            variant='primary'
                            disabled={loading}
                            className='d-flex align-items-center fs_12'
                            style={{ height: '30px' }}
                            onClick={handleSubmit}
                        >
                            Submit For Review
                        </CustomButton>
                    </div>
                </div>
            </div>

            <div className='border-top' style={{ height: "80vh", overflowY: "auto" }}>
                {(isLoading || loading) ? (
                    <div className="d-flex justify-content-around align-items-center" style={{ height: "100%" }}>
                        <Spinner animation="border" variant="primary" />
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <Table borderless className="mb-0" style={{ borderCollapse: 'separate', borderSpacing: '5px' }}>

                        <tbody>
                            {sentencePairs.map((sentencePair, index) => (
                                <tr key={`sentence-${sentencePair.paraId}-${index}`}>
                                    <td
                                        style={{
                                            width: '50%',
                                            verticalAlign: "top",
                                            border: "1px solid #dee2e6",
                                            backgroundColor: selectedId === index ? "rgb(236, 243, 251)" : "#ffff",
                                            padding: '8px'
                                        }}
                                    >
                                        <div style={{ fontSize: '14px', lineHeight: '1.5', textAlign: "start" }}>
                                            {sentencePair.source}
                                        </div>
                                    </td>
                                    <td
                                        style={{
                                            width: '50%',
                                            verticalAlign: "top",
                                            border: "1px solid #dee2e6",
                                            backgroundColor: selectedId === index ? "rgb(236, 243, 251)" : "#ffff",
                                            padding: '8px'
                                        }}
                                        onClick={() => setSelectedId(index)}
                                    >
                                        <DocxTextBlock
                                            index={index}
                                            sentencePair={sentencePair}
                                            onUpdate={handleTextBlockUpdate}
                                            isSelected={selectedId === index}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default DocxEditor;











// import { useEffect, useMemo, useState } from 'react';
// import CustomButton from '../../button/button';
// import { availableLanguages } from '../../data/languageData';
// import { useNavigate } from 'react-router-dom';
// import { useDispatch } from 'react-redux';
// import successToast from '../../toasts/successToast';
// import errorToast from '../../toasts/errorToast';
// import { getWordTextBlocksAction, updateWordFileAction } from '../../../redux/actions/translationAction';
// import { Spinner, Table } from 'react-bootstrap';
// import DocxSourceTextBlock from './docx-source-text';
// import DocxTextBlock from './docx-text-block';
// import { filterUpdatedTextblocks } from '../../helper';

// const DocxEditor = ({ documentData, sourceFileUrl, translatedFileUrl, job_id, isLoading }) => {
//     const [docxTextBlocks, setDocxTextBlocks] = useState([]);
//     const [translatedTextBlocks, setTranslatedTextBlocks] = useState([]);
//     const [sourceTextBlocks, setSourceTextBlocks] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [editedHistory, setEditedHistory] = useState(new Map());
//     const [selectedId, setSelectedId] = useState(null)
//     const navigate = useNavigate();
//     const dispatch = useDispatch();

//     useEffect(() => {
//         if (translatedFileUrl) {
//             setLoading(true);
//             dispatch(getWordTextBlocksAction({ translatedFileUrl: translatedFileUrl, sourceFileUrl: sourceFileUrl }, onFetchSuccess, onError));
//         }
//     }, [translatedFileUrl]);

//     const groupByParaId = (textBlocks) => {
//         const paraMap = new Map();
//         textBlocks.forEach(block => {
//             if (!paraMap.has(block.paraId)) {
//                 paraMap.set(block.paraId, []);
//             }
//             paraMap.get(block.paraId).push(block);
//         });
//         return Array.from(paraMap.values());
//     };

//     const groupSentences = (textBlocks) => {
//         const groups = [];
//         let currentGroup = [];
//         let currentParaId = null;
//         let currentSentId = null;

//         for (const block of textBlocks) {
//             if (block.paraId !== currentParaId || block.sentId !== currentSentId) {
//                 if (currentGroup.length > 0) {
//                     groups.push(currentGroup);
//                 }
//                 currentGroup = [block];
//                 currentParaId = block.paraId;
//                 currentSentId = block.sentId;
//             } else {
//                 currentGroup.push(block);
//             }
//         }

//         if (currentGroup.length > 0) {
//             groups.push(currentGroup);
//         }

//         return groups;
//     };

//     const groupedTranslatedSentences = useMemo(() => {
//         return groupSentences(translatedTextBlocks);
//     }, [translatedTextBlocks]);

//     const groupedSourceSentences = useMemo(() => {
//         return groupSentences(sourceTextBlocks);
//     }, [sourceTextBlocks]);

//     const getNormalizedGroups = () => {
//         if (groupedSourceSentences.length === groupedTranslatedSentences.length) {
//             return {
//                 source: groupedSourceSentences,
//                 translated: groupedTranslatedSentences
//             };
//         }
//         return {
//             source: groupByParaId(sourceTextBlocks),
//             translated: groupByParaId(translatedTextBlocks)
//         };
//     };

//     const normalizedGroups = useMemo(() => getNormalizedGroups(),
//         [groupedSourceSentences, groupedTranslatedSentences, sourceTextBlocks, translatedTextBlocks]);

//     const onFetchSuccess = (data) => {
//         setDocxTextBlocks(data.data.data.translatedTextBlocks);
//         setTranslatedTextBlocks(data.data.data.translatedTextBlocks);
//         setSourceTextBlocks(data.data.data.sourceTextBlocks);
//         setLoading(false);
//     };

//     const handleTextBlockUpdate = (index, updatedBlocks) => {
//         setDocxTextBlocks(prev => {
//             const newBlocks = [...prev];
//             const isGroupedByPara = groupedSourceSentences.length !== groupedTranslatedSentences.length;

//             // Only proceed if there are actual changes
//             const hasChanges = updatedBlocks.some(updatedBlock => {
//                 const originalBlock = prev.find(b => b.id === updatedBlock.id);
//                 return originalBlock && originalBlock.text !== updatedBlock.text;
//             });

//             if (!hasChanges) return newBlocks;

//             // For paragraph-level updates
//             if (isGroupedByPara) {
//                 const paraId = updatedBlocks[0]?.paraId;
//                 if (!paraId) return newBlocks;

//                 // Find all blocks with this paraId
//                 const originalParaBlocks = prev.filter(b => b.paraId === paraId);
//                 const updatedParaBlocks = updatedBlocks.filter(b => b.paraId === paraId);

//                 if (originalParaBlocks.length > 0 && updatedParaBlocks.length > 0) {
//                     const originalText = originalParaBlocks.map(b => b.text).join(' ');
//                     const updatedText = updatedParaBlocks.map(b => b.text).join(' ');

//                     // Find corresponding source paragraph using the same paraId
//                     const sourceParaBlocks = sourceTextBlocks.filter(b => b.paraId === paraId);
//                     const sourceText = sourceParaBlocks.length > 0
//                         ? sourceParaBlocks.map(b => b.text).join(' ')
//                         : "Source text not available";

//                     // Update all blocks in this paragraph
//                     updatedParaBlocks.forEach(updatedBlock => {
//                         const originalIndex = newBlocks.findIndex(
//                             b => b.id === updatedBlock.id
//                         );
//                         if (originalIndex !== -1) {
//                             newBlocks[originalIndex] = { ...updatedBlock };
//                         }
//                     });

//                     setEditedHistory(prev => {
//                         const newHistory = new Map(prev);
//                         const historyKey = `para-${paraId}`;

//                         newHistory.set(historyKey, {
//                             source: sourceText,
//                             translated: originalText,
//                             updated: updatedText
//                         });

//                         console.log("Paragraph update history:", {
//                             key: historyKey,
//                             sourceText,
//                             originalText,
//                             updatedText
//                         });

//                         return newHistory;
//                     });
//                 }
//             }
//             // For sentence-level updates
//             else {
//                 updatedBlocks.forEach(updatedBlock => {
//                     const originalIndex = newBlocks.findIndex(
//                         b => b.id === updatedBlock.id
//                     );

//                     if (originalIndex !== -1 && newBlocks[originalIndex].text !== updatedBlock.text) {
//                         const originalText = newBlocks[originalIndex].text;
//                         newBlocks[originalIndex] = { ...updatedBlock };

//                         // Find corresponding source sentence using both paraId and sentId
//                         const sourceBlock = sourceTextBlocks.find(b =>
//                             b.paraId === updatedBlock.paraId &&
//                             b.sentId === updatedBlock.sentId
//                         );

//                         const sourceText = sourceBlock?.text || sourceTextBlocks[originalIndex]?.text;

//                         setEditedHistory(prev => {
//                             const newHistory = new Map(prev);
//                             const historyKey = updatedBlock.id;

//                             newHistory.set(historyKey, {
//                                 source: sourceText,
//                                 translated: originalText,
//                                 updated: updatedBlock.text
//                             });

//                             console.log("Sentence update history:", {
//                                 key: historyKey,
//                                 sourceText,
//                                 originalText,
//                                 updatedText: updatedBlock.text,
//                                 sourceBlock,
//                                 updatedBlock
//                             });

//                             return newHistory;
//                         });
//                     }
//                 });
//             }
//             return newBlocks;
//         });
//     };


//     const handleSubmit = () => {
//         setLoading(true);
//         const updatedParts = Array.from(editedHistory.values());
//         const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);
//         console.log('filteredUpdatedParts :>> ', filteredUpdatedParts);
        
//         if (updatedParts.length > 0) {
//             dispatch(updateWordFileAction({
//                 documentUrl: translatedFileUrl,
//                 updatedTextBlocks: docxTextBlocks,
//                 updatedParts: filteredUpdatedParts,
//                 outputPath: `${job_id}.docx`,
//                 job_id: job_id
//             }, onSuccess, onError));
//         } else {
//             navigate('/post-edit/summary', { state: { job_id: job_id } });
//         }
//     };

//     const handleSaveDraft = () => {
//         setLoading(true);
//         const updatedParts = Array.from(editedHistory.values());
//         const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);
//         dispatch(updateWordFileAction({
//             documentUrl: translatedFileUrl,
//             updatedTextBlocks: docxTextBlocks,
//             updatedParts: filteredUpdatedParts,
//             outputPath: `${job_id}.docx`,
//             job_id: job_id
//         }, onSaveSuccess, onError));
//     };

//     const onSuccess = (data) => {
//         setLoading(false);
//         successToast(data.data.message);
//         const job = data.data.data;
//         const updatedParts = Array.from(editedHistory.values());
//         navigate('/post-edit/summary', {
//             state: {
//                 updatedParts: updatedParts,
//                 sourceLang: job.source_lang,
//                 targetLang: job.target_lang,
//                 job_id: job_id
//             }
//         });
//         setEditedHistory(new Map());
//     };

//     const onSaveSuccess = (data) => {
//         setEditedHistory(new Map());
//         dispatch(getWordTextBlocksAction({ translatedFileUrl: data?.data?.data?.signed_url, sourceFileUrl: sourceFileUrl }, onFetchSuccess, onError));
//         successToast(data.data.message);
//     };

//     const onError = (data) => {
//         setLoading(false);
//         errorToast(data?.data?.message);
//     };


//     return (
//         <div className='bg-white h-100 border rounded-4 m-2'>
//             <div className='d-flex justify-content-between w-100 align-items-center p-2'>
//                 <div className='d-flex gap-4 w-100 align-items-center'>
//                     <CustomButton
//                         disabled={true}
//                         variant='primary'
//                         className='d-flex align-items-center cursor_pointer fs_12'
//                         style={{ height: '30px' }}
//                     >
//                         {availableLanguages.find(lang => lang?.value === documentData?.source_lang)?.label ?? "Loading..."}
//                     </CustomButton>
//                 </div>
//                 <div className='d-flex justify-content-between gap-4 w-100 align-items-center'>
//                     <div className='px-3'>
//                         <CustomButton
//                             disabled={true}
//                             variant='primary'
//                             className='d-flex align-items-center cursor_pointer fs_12'
//                             style={{ height: '30px' }}
//                         >
//                             {availableLanguages.find(lang => lang?.value === documentData?.target_lang)?.label ?? "Loading..."}
//                         </CustomButton>
//                     </div>
//                     <div className='d-flex gap-3'>
//                         <CustomButton variant='primary' disabled={loading} className='d-flex align-items-center fs_12' style={{ height: '30px' }} onClick={handleSaveDraft}>
//                             Save Draft
//                         </CustomButton>
//                         <CustomButton variant='primary' disabled={loading} className='d-flex align-items-center fs_12' style={{ height: '30px' }} onClick={handleSubmit}>
//                             Submit For Review
//                         </CustomButton>
//                     </div>
//                 </div>
//             </div>

//             <div className='border-top' style={{ height: "80vh", overflowY: "auto" }}>
//                 {(isLoading || loading) ? (
//                     <div className="d-flex justify-content-around align-items-center" style={{ height: "100%" }}>
//                         <Spinner animation="border" variant="primary" />
//                         <Spinner animation="border" variant="primary" />
//                     </div>
//                 ) : (
//                     <Table borderless className="mb-0" style={{ borderCollapse: 'separate', borderSpacing: '5px' }}>
//                         <tbody>
//                             {normalizedGroups.source.map((sourceBlock, index) => {
//                                 const translatedBlock = normalizedGroups.translated[index] || [];
//                                 return (
//                                     <tr key={`row-${index}`} >
//                                         <td style={{ width: '50%', verticalAlign: "top", border: "1px solid #dee2e6", backgroundColor: selectedId === index ? "rgb(236, 243, 251)" : "#ffff" }}>
//                                             <DocxSourceTextBlock
//                                                 key={`source-${sourceBlock[0].paraId}-${sourceBlock[0].sentId}`}
//                                                 block={sourceBlock}
//                                             />
//                                         </td>
//                                         <td
//                                             style={{
//                                                 width: '50%',
//                                                 verticalAlign: "top",
//                                                 border: "1px solid #dee2e6",
//                                                 wordWrap: "break-word",          // ✅ Enables word breaking
//                                                 overflowWrap: "break-word",      // ✅ Modern equivalent
//                                                 whiteSpace: "normal",
//                                                 backgroundColor: selectedId === index ? "rgb(236, 243, 251)" : "#ffff"
//                                             }}
//                                             onClick={() => setSelectedId(index)} >
//                                             {translatedBlock.length > 0 ? (
//                                                 <DocxTextBlock
//                                                     index={index}
//                                                     key={`translated-${index}`}
//                                                     onUpdate={handleTextBlockUpdate}
//                                                     block={translatedBlock}
//                                                 />
//                                             ) : (
//                                                 <div style={{ minHeight: '40px' }}></div>
//                                             )}
//                                         </td>
//                                     </tr>
//                                 );
//                             })}
//                         </tbody>
//                     </Table>
//                 )}
//             </div>
//         </div>
//     );
// };

// export default DocxEditor;